import { brandingFromSettings, readBranding, saveBranding } from './utils/tenantBranding';
import { displayLocale, formatDate, tenantTimezone } from './utils/regional';
import { withTimeout } from './utils/withTimeout'; //the sign-up calls' bound - why, in that file
import { makeObservable, observable, action, toJS} from 'mobx';
import { feathers } from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import io from 'socket.io-client';
import authentication from '@feathersjs/authentication-client'
import { rx } from 'feathers-reactive';
import { API_URL, ADMIN_ID, DEFAULT_EMAIL, DEFAULT_PSW, QR_READ_ONLY, TABLE_QR_ORDERING_ONLY, PHONE_REGEX } from "./assets/globalConstants";
import Utils from "./Utils";
import { toString } from 'diacritic-regex';
import { v4 as uuidv4 } from 'uuid';
import {commonEL} from "./locales/el/common";
import {commonEN} from "./locales/en/common";
import {commonDE} from "./locales/de/common";
import {commonES} from "./locales/es/common";
import {commonFR} from "./locales/fr/common";
import {commonIT} from "./locales/it/common";
import {commonAR} from "./locales/ar/common";
import {commonAL} from "./locales/sq/common";
import {commonTR} from "./locales/tr/common";
import {commonBG} from "./locales/bg/common";

const localeMap = { el: commonEL, en: commonEN, de: commonDE, es: commonES, fr: commonFR, it: commonIT, ar: commonAR, sq: commonAL, tr: commonTR, bg: commonBG };

// How long the header keeps offering to track the last order. A delivery that
// is still "in flight" a day later is finished in every practical sense, and
// the tracking service stops returning a position long before this anyway.
const LATEST_ORDER_TTL_MS = 24 * 60 * 60 * 1000;

const diacriticsMappings ={
  'α': 'αάΑΆ',
  'ε': 'εέΈΕ',
  'η': 'ηήΗΉ', 
  'ι': 'ιίΙΊϊιϊΪ',
  'ο': 'οόΟΌ',
  'υ': 'υύΥΎϋΫ',
  'ω': 'ωώΩΏ'  
}

class FeathersStore{  
   
    favoritesArray = [];
    wishlistId = "";
    chatId = "";
    hasLocationPermission= false;
    isAuthenticated= false;
    user = null;
    categories = [];
    settings = {};
    branding = readBranding(API_URL);

    //QR mode, settings-first with the build constant as fallback. `settings` starts as {} and
    //fills one round-trip after mount, so before it arrives these read undefined and fall back
    //to exactly what constants.js says today - no flash, no behaviour change on release.
    //Deliberately `??` and not `||`: a deliberate false in settings must win over a true build.
    qrReadOnly = () => this.settings?.QR_READ_ONLY ?? QR_READ_ONLY;
    tableQrOrderingOnly = () => this.settings?.TABLE_QR_ORDERING_ONLY ?? TABLE_QR_ORDERING_ONLY;

    //Bundle-parts rule (bundle-vat-v2 §1-§2), read by the cart creators only, at the moment a line is
    //built: spread next to product_vat with the predicate that chose it. While the AppDate switch is on,
    //EVERY new line says whether it was priced in package context (then a bundle's parts are filed at
    //their own packageVat). Switch off - or VIVA_FISCAL, which files whole lines - writes no key, and a
    //line without the key stays legacy for every issuer. (VIVA_FISCAL is not in the public settings
    //today; the server refuses the combination, this only mirrors the contract.)
    lineMarker = (packagePriced) => (this.settings?.bundlePartsRule === true && !this.settings?.VIVA_FISCAL)
      ? { product_packagePricing: !!packagePriced }
      : {};

    //The app-download section (store QR codes + badges). Replaces the old build-time HIDE_QR, so the
    //shop is now gated by the SAME settings flag the dashboard uses for its mobile-QR button
    //(showMobileQR) - one switch, one truth, instead of a constants.js edit and a republish.
    //
    //No `??` fallback here on purpose, unlike the two above: HIDE_QR_CODES was `true` on all 76
    //storefronts, so falling back to it would keep the section hidden everywhere and make the
    //settings flag useless. The flag was backfilled onto every tenant that has a dashboard
    //(2026-08-21) precisely so this can read settings alone; a tenant with no key reads false =
    //hidden, which is what those tenants show today anyway.
    //
    //There is nothing to show without at least one store link, so an enabled shop that has not
    //filled either URL renders nothing rather than an empty frame.
    androidStoreUrl = () => `${this.settings?.ANDROID_URL || ''}`.trim();
    iosStoreUrl = () => `${this.settings?.IOS_URL || ''}`.trim();
    showAppDownload = () => !!this.settings?.showMobileQR
      && !!(this.androidStoreUrl() || this.iosStoreUrl());
    cartLength = 0;
    goToAddressHelper = false;
    orderItem = {};
    searchCategory = {}; //active search category ;  
    language = "el";
    productUpdatedEvent = {};  
    tablePatchedEvent = {};
    //The sign-up waiting for its code {applicationId, codeDigits, email, password, ...}. Home renders the code
    //screen from it: registration signs out first, and a screen owned by a guest-only part of the page (the
    //checkout's 'register here') was unmounted by that very sign-out.
    pendingSignup = null;
    //Counts every sign-in and sign-out (login/logout below). Not observable: utils/guestSession.js reads it to tell
    //whether anyone signed in or out while a guest sign-in was put off to the next connect.
    authEpoch = 0;
    preOrderBooking = null; //set after a successful booking; routes checkout to attach the order to the booking instead of the table
    userUpdatedEvent = {};
    productCreatedEvent = {}; 
    messageCreatedEvent = {}; 
    showCookies = true;
    tableId = "eshop";
    tableSessionStart = 0; //ms epoch of entering the CURRENT table session (not persisted/observable)
    restaurantOpen = true;
    showRestaurantMessage = true;
    discountsArray = [];
    currency = "€";
    orderCode = null;
    latestOrder = null;   //{id, token} of the order just placed - drives the header "track" affordance
    selectedBranch = undefined;

    versionNumber = "1.0.119";
    
    constructor(){
      // Rehydrate O&P table mode synchronously (localStorage 'tableId', {value, expiry} format)
      // so the FIRST product fetches use the right catalog filter on any route reload.
      // HomeBody only restores tableId when a __table param is present; /product/:name and
      // /Home/:name reloads never did, silently reverting QR customers to the eshop catalog.
      try{
        const itemStr = localStorage.getItem('tableId');
        if(itemStr){
          const item = JSON.parse(itemStr);
          if(new Date().getTime() > item.expiry){
            // Expired QR session: removing the key here would silence the components'
            // own expiry-triggered purge (cartTable/Checkout/HomeBody), so purge the
            // forgotten table cart in the same breath.
            localStorage.removeItem('tableId');
            localStorage.setItem('fdwa_cart', JSON.stringify([]));
          }
          else if(item.value){
            this.tableId = item.value;
            this.tableSessionStart = new Date().getTime(); //reload with a valid key restarts the age cap
          }
        }
      }catch(e){ /* corrupt entry - stay in eshop mode */ }
      try{ //rehydrate a pending booking pre-order (same {value, expiry} format)
        const poStr = localStorage.getItem('preOrderBooking');
        if(poStr){
          const po = JSON.parse(poStr);
          if(new Date().getTime() > po.expiry) localStorage.removeItem('preOrderBooking');
          else if(po.value) this.preOrderBooking = po.value;
        }
      }catch(e){}
      try{ //rehydrate the last placed order so the header "track" affordance
           //survives a reload and the Viva payment redirect (same format again)
        const loStr = localStorage.getItem('fdwa_latest_order');
        if(loStr){
          const lo = JSON.parse(loStr);
          if(new Date().getTime() > lo.expiry) localStorage.removeItem('fdwa_latest_order');
          else if(lo.value?.id) this.latestOrder = lo.value;
        }
      }catch(e){ /* corrupt entry - no header affordance, nothing else breaks */ }
      makeObservable(this,  {
        favoritesArray: observable,
        wishlistId: observable,
        hasLocationPermission: observable,
        isAuthenticated: observable,
        user: observable,
        categories: observable,
        settings: observable,
        branding: observable,
        cartLength: observable,
        goToAddressHelper: observable,
        orderItem: observable,
        searchCategory: observable, 
        language: observable, 
        productUpdatedEvent: observable,
        tablePatchedEvent: observable,
        preOrderBooking: observable,
        pendingSignup: observable,
        userUpdatedEvent: observable,
        productCreatedEvent: observable,  
        messageCreatedEvent: observable, 
        showCookies: observable,
        chatId: observable,
        tableId: observable,  
        restaurantOpen: observable, 
        showRestaurantMessage: observable,
        discountsArray: observable,
        currency: observable,
        orderCode: observable,
        latestOrder: observable,
        selectedBranch: observable,
        versionNumber: observable,
        setFavoritesArray: action,
        setWishlistId: action,
        setHasLocationPermission: action,
        setIsAuthenticated: action,
        setUser: action,
        setCategories: action,
        setSettings: action,
        setBranding: action,
        setObservables: action,
        setCartLength: action,
        setGoToAddressHelper: action,
        setOrderItem: action,
        setSearchCategory: action,
        setLanguage: action,
        setProductUpdatedEvent: action,
        setTablePatchedEvent: action,
        setPreOrderBooking: action,
        setPendingSignup: action,
        setUserUpdatedEvent: action,
        setProductCreatedEvent: action, 
        setMessageCreatedEvent: action,   
        setShowCookies: action,
        setTableId: action,
        setChatId: action,
        setRestaurantOpen: action, 
        setShowRestaurantMessage: action,
        setDiscountsArray: action,
        setCurrency: action,
        setOrderCode: action,
        setLatestOrder: action,
        clearLatestOrder: action,
        replaceOrderItem: action,
        setSelectedBranch: action,
        resetOrderItem: action
      })
      this.fetchChatId();// used for anonymous chat 
    }

    setChatId = value => {
      this.chatId = value;
    }

    // UUIDv4 from the platform CSPRNG. crypto.randomUUID needs a secure context
    // (https / localhost); fall back to getRandomValues, then to a last-resort
    // Math.random form so an order is never blocked from being placed.
    newTrackingToken = () => {
      try{
        if(window.crypto?.randomUUID) return window.crypto.randomUUID();
        if(window.crypto?.getRandomValues){
          const b = new Uint8Array(16);
          window.crypto.getRandomValues(b);
          b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
          const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
          return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
        }
      }catch(e){ /* fall through */ }
      return `t-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    }

    /* The order the customer just placed: {id, token}. Persisted so the header
       "track" affordance survives a reload and the Viva payment redirect, and
       so it works for a GUEST, who has no account to look the order up under.
       Cleared once the order is delivered (or after LATEST_ORDER_TTL_MS) so the
       header does not advertise tracking for a finished order. */
    setLatestOrder = (id, token) => {
      if(!id){ this.latestOrder = null; return; }
      this.latestOrder = { id: `${id}`, token: `${token || ''}` };
      try{
        localStorage.setItem('fdwa_latest_order', JSON.stringify({
          value: this.latestOrder, expiry: new Date().getTime() + LATEST_ORDER_TTL_MS
        }));
      }catch(e){ /* storage disabled - header affordance just won't persist */ }
    }

    clearLatestOrder = () => {
      this.latestOrder = null;
      try{ localStorage.removeItem('fdwa_latest_order'); }catch(e){}
    }

    setOrderItem = data => {
      const clonedOrderItem = toJS(this.orderItem);
      const mergedObject = {...clonedOrderItem, ...data};
      this.orderItem = mergedObject;
    }

    replaceOrderItem = data => {
      this.orderItem = data;
    }

    resetOrderItem = () => {     
      this.orderItem = {};
    }

    setGoToAddressHelper = value => {
      this.goToAddressHelper = value;
    }

    setFavoritesArray = value => {
      this.favoritesArray = value;
    }

    setWishlistId = value => {
     this.wishlistId = value;
    }

    setHasLocationPermission = value => {
      this.hasLocationPermission = value;
    }

    setIsAuthenticated = value => {
      this.isAuthenticated = value;
    }

    setUser = value => {
      this.user = value;
    }
    
    setCategories = value => {
      this.categories = value;
    }

    setBranding = value => {
      this.branding = brandingFromSettings(value);
      saveBranding(API_URL, this.branding);
    }

    setSettings = value => {
      this.settings = value;
    }

    setCartLength = value => {
      this.cartLength = value;
    }

    setLanguage = value => {
      this.language = value;
      localStorage.setItem('Language', value);  
    }

    setShowCookies = data => {      
      this.showCookies = data;
    }

    setProductUpdatedEvent = data => {      
      this.productUpdatedEvent = data;
    }

    setTablePatchedEvent = data => {
      this.tablePatchedEvent = data;
    }

    setPendingSignup = data => {
      this.pendingSignup = data || null;
    }

    setPreOrderBooking = data => {
      this.preOrderBooking = data;
      try{
        if(data){
          //the pre-order session lives until the BOOKING HOUR - customers may return any
          //time before their reservation to order; at the hour the session ends.
          const bookingMs = new Date(data.deferredTime).getTime();
          const expiry = Number.isFinite(bookingMs) ? bookingMs : new Date().getTime() + 6 * 60 * 60 * 1000;
          localStorage.setItem('preOrderBooking', JSON.stringify({value: data, expiry}));
        }
        else localStorage.removeItem('preOrderBooking');
      }catch(e){}
    }

    setUserUpdatedEvent = data => {      
      this.userUpdatedEvent = data;
    }

    setProductCreatedEvent = data => {      
      this.productCreatedEvent = data;
    }

    setMessageCreatedEvent = data => {      
      this.messageCreatedEvent = data;
    }  

    setSearchCategory = data => {      
      this.searchCategory = data;
    }

    setTableId = value => {
      // Stamp the session start when ENTERING table mode (new table) — the absolute age cap in
      // liveTableSessionValid() uses it to stop a suspended-but-never-reloaded tab from
      // resurrecting yesterday's table session out of its still-live JS heap.
      if(value !== 'eshop' && value !== this.tableId) this.tableSessionStart = new Date().getTime();
      this.tableId = value;
      if(this.tableId !== 'eshop'){
        this.setShowRestaurantMessage(false)
        this.restaurantOpen = true;
      }
    }

    // An in-memory table session may outlive its localStorage key (TTL lapse, ITP purge) and is
    // then authoritative — but only within an absolute age cap, so a stale suspended tab from a
    // previous day can never re-mint itself onto the physical table.
    liveTableSessionValid = () =>
      this.isTableOrder() &&
      (new Date().getTime() - (this.tableSessionStart || 0)) < 24 * 60 * 60 * 1000;

    // Sliding table-session TTL: re-persist the localStorage 'tableId' with a fresh 6h expiry on
    // every cart/checkout touch, so a long table visit never expires mid-session and silently
    // degrades into the eshop checkout. No-op in eshop mode. Same {value, expiry} format as
    // HomeBody's setWithExpiry and the constructor rehydrate.
    touchTableSession = () => {
      if(!this.liveTableSessionValid()) return;
      try{
        let expiry = new Date().getTime() + 6 * 60 * 60 * 1000;
        // A pre-order (reservation) session is bounded by the BOOKING HOUR, not the sliding 6h:
        // never truncate a booking further away than 6h (the customer may return any time before
        // it) and never extend past the hour (a post-hour order would land on the PHYSICAL
        // table's live bill instead of attaching to the booking).
        if(this.preOrderBooking?.tableId === this.tableId){
          const bookingMs = new Date(this.preOrderBooking?.deferredTime).getTime();
          if(Number.isFinite(bookingMs)){
            if(bookingMs <= new Date().getTime()) return; //booking hour passed - session is ending, don't re-arm
            expiry = bookingMs;
          }
        }
        localStorage.setItem('tableId', JSON.stringify({ value: this.tableId, expiry }));
      }catch(e){ /* storage unavailable - in-memory mode still holds the table */ }
    }

    // O&P (QR table) mode iff tableId is truthy and not 'eshop' (null-safe: cartTable may set null).
    isTableOrder = () => !!this.tableId && this.tableId !== 'eshop';

    // Catalog rule: O&P shows restaurant products ({enabled}, like PDA/dashboard orderTaking);
    // eshop shows delivery products ({enabledForDelivery}, like the customer mobile app).
    // Strict equality on purpose - missing flag means hidden, the platform-wide convention.
    productVisibilityQuery = () => this.isTableOrder() ? { enabled: true } : { enabledForDelivery: true };

    isProductVisible = p => this.isTableOrder() ? !!p?.enabled : !!p?.enabledForDelivery;

    // Daypart (lunch/dinner) menu filtering — applies ONLY inside a web-app reservation
    // (pre-order booking) session. Returns 'lunch' | 'dinner' when the booked time falls in
    // the settings-defined window, else null (no booking, gap time, or windows unset) → no
    // filtering. The booking ISO's wall time (T HH:MM) is the customer-selected local time.
    _toDayMinutes = (hhmm) => {
      if(!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
      const [h, m] = hhmm.split(':');
      const hh = +h, mm = +m;
      if(isNaN(hh) || isNaN(mm)) return null;
      return hh * 60 + mm;
    };

    bookingDaypart = () => {
      // Only inside the booking's pre-order TABLE session (orderForBooking sets the
      // booked table as tableId). A merely-pending booking persisted in localStorage
      // must NOT filter the normal eshop/delivery catalog (tableId === 'eshop').
      if(!this.isTableOrder()) return null;
      const dt = this.preOrderBooking?.deferredTime;
      if(!dt) return null;
      const match = String(dt).match(/T(\d{2}):(\d{2})/);
      if(!match) return null;
      const mins = (+match[1]) * 60 + (+match[2]);
      const inWindow = (start, end) => {
        const a = this._toDayMinutes(start), b = this._toDayMinutes(end);
        if(a == null || b == null) return false;
        // b < a means the window crosses midnight.
        return b >= a ? (mins >= a && mins < b) : (mins >= a || mins < b);
      };
      if(inWindow(this.settings?.lunchStart, this.settings?.lunchEnd)) return 'lunch';
      if(inWindow(this.settings?.dinnerStart, this.settings?.dinnerEnd)) return 'dinner';
      return null;
    };

    // A category shows unless we're in a booking session whose daypart conflicts with the
    // category's own daypart tag. Untagged / 'all' categories always show.
    categoryMatchesDaypart = (category) => {
      const dp = this.bookingDaypart();
      if(!dp) return true;
      const cdp = category?.options?.daypart;
      if(!cdp || cdp === 'all') return true;
      return cdp === dp;
    };

    setRestaurantOpen = value => {
      if(this.tableId === 'eshop'){
        if(!value && this.restaurantOpen)this.setShowRestaurantMessage(true)
        this.restaurantOpen = value;
      }else{
        this.setShowRestaurantMessage(false)
        this.restaurantOpen = true;
      }   
    
    }
    setShowRestaurantMessage = value => {
      this.showRestaurantMessage = value;
    }

    setDiscountsArray = value => {
      this.discountsArray = value;
    }

    setCurrency = value => {
      this.currency = value;
    }

    setOrderCode = value => {
      this.orderCode = value;
    }

    setSelectedBranch = value => {
      this.selectedBranch = value;
    }
   
    connect = async () => {

      //ESTABLISH FEATHERS CONNECTION

     // const options = {transports: ['websocket'],forceNew: true, pingTimeout: 3000, pingInterval: 5000};
      this.app = feathers();   
                     
      this.socket = io(API_URL,  {query: {chatId: this.chatId}});    
      // this.feathersAuthClient = require('@feathersjs/authentication-client').default;
 
        
        this.app
            .configure(socketio(this.socket)) 
           // .configure(feathers.socketio(this.socket))
            .configure( authentication({            
                storage: window.localStorage // To store our accessToken
            }))
            .configure(rx({                           // add feathers-reactive plugin
                idField: '_id'
            }));

        this.isConnecting = true;
    
      await this.setObservables();
      
      this.app.io.on('connect', async () => {
        this.isConnecting = false;
        try{
          await this.setObservables();
          console.log('authenticated after reconnection');
        }catch(error) {
          console.log('error authenticating after reconnection', error);
        };
      });

      this.app.io.on('disconnect', () => {
        console.log('disconnected');
        this.isConnecting = true;
      });

      this.app.service('products').on('updated', message => this.setProductUpdatedEvent(message));
      this.app.service('products').on('patched', message => this.setProductUpdatedEvent(message));
      this.app.service('products').on('created', message => this.setProductCreatedEvent(message));
      this.app.service('messages').on('created', message => this.setMessageCreatedEvent(message));
      this.app.service('tables').on('patched', message => this.setTablePatchedEvent(message));
      this.app.service('timer').on('updated', message => this.setRestaurantOpen(message));
      this.app.service('settings').on('patched', async message => await this.fetchSettings());
      // Adopt a remote user event ONLY for this session's own user, and never for the SHARED
      // anonymous guest: every visitor is logged in as the same 'default' user, so a patch by
      // one guest used to swap the user object on every other guest mid-session (setUser also
      // ran unconditionally, replacing the session user with ANY customer's patched doc).
      this.app.service('users').on('patched', message => {
        if(message._id === this.user?._id && this.user?.email !== DEFAULT_EMAIL){
          this.setUserUpdatedEvent(message);
          this.setUser(message);
        }
      });
      this.app.service('users').on('updated', message => {
        if(message._id === this.user?._id && this.user?.email !== DEFAULT_EMAIL){
          this.setUserUpdatedEvent(message);
          this.setUser(message);
        }
      });
      this.app.service('users').on('removed', message => {
        if(message._id === this.user?._id)
          this.login(DEFAULT_EMAIL, DEFAULT_PSW)
          .then(async(auth) => {         
            await this.setObservables();                          
          })
          .catch(error => window.alert(`${(localeMap[this.language] || commonEN).loginError}${error.message}`));
      });   
    }
  
    setObservables = async() => {
       try{
          const auth = await this.app.reAuthenticate();
          const { user } = await this.app.get('authentication');
          this.setUser(user);          
          this.user && (this.setIsAuthenticated(true));  
          const tree = await this.app.service('categories').get("5d0367d6d31be01e208545a6");          
          this.setCategories(tree.youTree);    
          await this.fetchSettings();              
          const data = await this.wishlist();               
          data && (this.setFavoritesArray(data[0].items));          
          data && (this.setWishlistId(data[0]._id));  
          //console.log(this.socket.id)                        
        }catch{

        }
    }

    fetchSettings = async() => {
      const settingsArray = await this.app.service('settings').find(); 
      const settings = (settingsArray.data || settingsArray)[0] || {};
      this.setSettings(settings);
      this.setBranding(settings);
    }

    fetchChatId = () => {     
      let chatIdFromStorage = JSON.parse(localStorage.getItem('fdwa_chatId'));
      if(!chatIdFromStorage){
        chatIdFromStorage = uuidv4();
        localStorage.setItem("fdwa_chatId", JSON.stringify(chatIdFromStorage));
      }
      this.setChatId(chatIdFromStorage);
      return chatIdFromStorage;
    }
 
    login = async (email, password) => {
      //Same reasoning on the way IN: signing in (or dropping back to the guest account)
      //is an identity change too, and the incoming identity has not earned the outgoing
      //identity's discount.
      this.setDiscountsArray([]);
      this.authEpoch++;
        return this.app.authenticate({
            strategy: 'local',
            email,
            password
          });
    };

    //realm = null;
    
  
    logout = async () => {
      this.authEpoch++;
      await this.app.logout();
      this.setUser(null);
      this.setIsAuthenticated(false);
      this.setCategories(null);
      //An applied voucher belongs to the identity that applied it. Without this a 30% first-order
      //row survived a logout into the guest session, and the server refused the order at checkout
      //with the customer having no idea why. Cleared on BOTH directions of an identity change.
      this.setDiscountsArray([]);
      this.setSettings({});
      this.setGoToAddressHelper(false);
    }

    getAccessToken = async() => {
      return await this.app.get('authentication');
    }

    removeUser = async () => {
      const userId = this.user._id;
      await this.logout();      
      await this.login(DEFAULT_EMAIL, DEFAULT_PSW);   
      await this.setObservables();                                 
      await this.app.service("users").remove(userId);
    }

    //The server mails the sign-up code and answers {_id, processed, createdAt, codeDigits} - nothing else
    //of the application comes back (it holds the password).
    sendApplication = async (firstname, lastname, email, password, phone) => {
      return await this.app.service('applications').create({
        firstname, lastname, email, password, phone
      });
    }

    //The SERVER checks the code and makes the account (feathers-server services/verifications). This used to
    //be done here: read the code back from emaillog as the shared guest, compare it in the browser, call
    //users.create ourselves and patch the application processed - so the code proved nothing, and anyone
    //could skip it. A refusal carries error.data.reason (verificationCode.js maps it to a message).
    verifyApplication = async (applicationId, pin) => {
      return await withTimeout(this.app.service('verifications').create({ applicationId, pin }));
    }

    //A new code, mailed by the server to the address on the application.
    resendVerificationCode = async (applicationId) => {
      return await withTimeout(this.app.service('verifications').create({ applicationId, action: 'resend' }));
    }

    googleAuth(access_token){
      return this.app.authenticate({
        strategy: "google",
        accessToken: access_token
      })
    }

    getUserByEmail = async email => {
      return  await this.app.service('users')
      .find({
        query: { 
            email
        }
      })          
    }

    _t(word) {
      const locale = localeMap[this.settings.masterLanguage] || commonEN;
      return locale[word] || commonEN[word] || word;
    }

    sendEmail = async(userId, email, firstname, lastname) => {
      const alphabet = "abcdefghijklmnopqrstuvwxyz"
      const randomLower = alphabet[Math.floor(Math.random() * alphabet.length)];
      const randomUpper = alphabet[Math.floor(Math.random() * alphabet.length)].toUpperCase();
      const random6digit = Math.floor(100000 + Math.random() * 900000).toString();
      let newPassword = randomUpper + randomLower + random6digit;
      const shopName = this.settings.companyName || 'BringFood';
        const emailToSend = {
          userId,
          applicationId: 'FORGOT_PASSWORD',
          newPassword,
          from: `${this.settings.mailgunEmail}`,
          to: `${email}`,
          subject: `${shopName} - ${this._t('emailPasswordReset')}`,
          text: `${this._t('dear')} ${firstname} ${lastname},\n\n${this._t('emailNewPassword')}: ${newPassword}\n${this._t('emailLoginInfo')}.\n${this._t('emailChangePassword')}.\n\n${shopName}`,
          html: `<html><body style="font-family: Arial, sans-serif; font-size: 16px; color: #333;">
                <p>${this._t('dear')} ${firstname} ${lastname},</p>
                <p>${this._t('emailNewPassword')}: <strong>${newPassword}</strong></p>
                <p>${this._t('emailLoginInfo')}.</p>
                <p>${this._t('emailChangePassword')}.</p>
                <br><p>${shopName}</p>
              </body></html>`
       };
       try{
          return await this.app.service('emails').create(emailToSend);
        }catch(err){
          console.log('Could not send email FROM sendEmail method: ', err )
        };
    }

    async sendContactEmail( email, firstname, lastname, message, phone){

      //shop-facing email: Greek for Greek-master shops (or when unset), English otherwise
      const greekShop = !this.settings?.masterLanguage || this.settings?.masterLanguage === "el";

      let htmlContent =  `
        <html>
        <head>
        <style>
        body{  
          background-color:#c8d7e1;        
        }
        h2{
          background-color:hsl(217, 10%, 25%);
          color:#f1ebff;
          text-align: center;
        }        
        .message-container{
          border: 1px solid black;
          padding: 15px;
          text-align: left; 
          background-color: #f1f1c1;
        }
        .container{
          margin-top: 2vw;         
          padding: 2vw;
          border: 1px solid black;          
          font-size:16px;
          width: 60%;          
        }
        @media screen and (max-width: 992px) {
          .container{
            width: 95%;
          }
        }
        </style>
        </head>
        <body> 
        <div class="container">
          <h2>${greekShop ? "Νέο μήνυμα από το e-shop σας" : "New message from your e-shop"}</h2>
          <div class="message-container">
            ${greekShop ? `<p>Ο χρήστης ${firstname} ${lastname} ,</p>
            <p>  με email ${email} ${phone ? `και τηλέφωνο: ${phone}` : ""} ,</P>
            <p>έστειλε το εξής μήνυμα:   </p>` : `<p>The user ${firstname} ${lastname},</p>
            <p>with email ${email} ${phone ? `and phone: ${phone}` : ""},</p>
            <p>sent the following message:</p>`}
            <div>
                   ${message}
            </div>
          </div>
        </div>
        </body
      </html>`
     
        const emailToSend = {          
          from: `${this.settings.mailgunEmail}`,
          to: `${this.settings.email}`,
          subject: greekShop ? 'Μήνυμα από τη φόρμα επικοινωνίας του e-shop' : 'Message from the e-shop contact form',
          html: htmlContent 
       };    
      return await this.app.service('emails').create(emailToSend);
    }

    patchUser(userId, firstname, lastname, phone){
      const payload = {firstname, lastname, phone};
      return this.app.service('users').patch(userId, payload);
    }

    patchUserAvatar(userId, avatar){     
      return this.app.service('users').patch(userId, {avatar});
    }

    _patchUser(userId, payload){
      return this.app.service('users').patch(userId, payload);
    }

    patchProduct(productId, payload){      
      return this.app.service('products').patch(productId, payload);
    }

    async patchAddresses(userId, addresses){     
      return await this.app.service('users').patch(userId, {addresses});
    }

    async changePassword(newPassword){
      return await this.app.service('users')
        .patch(this.user._id, {password: newPassword});
    }

    getCategoriesTree(){
    return this.app.service('categories').get("5d0367d6d31be01e208545a6");
    }
    
    async productsPerCategory1(categoryId, searchText = ""){

      let query = {
        ...this.productVisibilityQuery(),
        "category1.id": categoryId
      };
    
      if(searchText){
        const index = this.settings.targetLanguages?.findIndex(lang => lang.code === this.language);
        this.language === this.settings.masterLanguage ?  
          Object.assign(query,
          {name: { $regex: toString({
              mappings: diacriticsMappings
          })(searchText), $options: 'i' }}
        ) :
        Object.assign(query,
          {[`nameTranslations.${index}.text`] : { $regex: toString()(searchText), $options: 'i' }})          
      }

      return await this.app.service('products')   
        .find({
            query
        }); 
    }

    async productsPerCategory2(categoryId, searchText = ""){

      let query = {
        ...this.productVisibilityQuery(),
        "category2.id": categoryId
      };
    
      if(searchText){
        const index = this.settings.targetLanguages?.findIndex(lang => lang.code === this.language);
        this.language === this.settings.masterLanguage ?  
        Object.assign(query,
          {name: { $regex: toString({
              mappings: diacriticsMappings
          })(searchText), $options: 'i' }}
        ) :
        Object.assign(query,
          {[`nameTranslations.${index}.text`] : { $regex: toString()(searchText), $options: 'i' }})   
      }
      
      return await this.app.service('products')   
        .find({
            query
        }); 
    }

    async products(searchText = ""){  

      let query = {
        ...this.productVisibilityQuery()
      }

      
      if(searchText){
        const index = this.settings.targetLanguages?.findIndex(lang => lang.code === this.language);
        this.language === this.settings.masterLanguage ?  
          Object.assign(query,
            {name: { $regex: toString({
                mappings: diacriticsMappings
            })(searchText), $options: 'i' }}
          ) :
          Object.assign(query,
            {[`nameTranslations.${index}.text`] : { $regex: toString()(searchText), $options: 'i' }})     
        }
  
      return await this.app.service('products')   
        .find({
            query
        }); 
    } 
    
    async _products() {
      return await this.app.service('products')   
      .find({}); 
    }

    async getProduct(productId){
      return await this.app.service('products').get(productId);
    }

    async offers(){
      return await this.app.service('products')   
      .find({
          query: {
           offer: true,
           ...this.productVisibilityQuery()
          }
        }); 
    }   

    createWishlist = async() => {
      const payload = {
        userId : this.user._id,
        items : this.favoritesArray
        };      
      return await this.app.service('wishlist').create(payload); 
    }

    updateWishlist = async() => {
      const payload = {
        userId: this.user._id,
        items: this.favoritesArray 
        };
      return await this.app.service('wishlist').update(this.wishlistId, payload); 
    }

    async wishlist(){
      return await this.app.service('wishlist')   
      .find({
          query: { 
           userId: this.user._id,  
          }
        }); 
    }

    async wishlistProducts(){     
      return await this.app.service('products')   
      .find({
          query: {
            _id: {
              $in: [...this.favoritesArray]
            },
            ...this.productVisibilityQuery()
          }
        }); 
    }

    createPaymentOrder = async(payload) => {
      return await this.app.service('pos')
        .create(payload, {query: {eshop: true}});
    }

    createStripe = async(payload) => {
      return await this.app
        .service('stripe')
        .create(payload);
    }

    getLastTransaction = async() => {
      return await this.app.service('vivahooks')
      .get("A");
    }

    getEshopTransaction = async(transactionId) => {
      return await this.app.service('pos')
      .get("", {query: {transactionId}}); 
    }

    createOrder = async(payload) => {
      /* Belt-and-braces tracking token. The server mints one in an orders
         before-create hook, but that hook is only as reliable as every tenant
         instance being up to date — and at least one box was observed running a
         stale orders.js while the rest of the build was current, which silently
         left orders untrackable. Minting here too costs nothing: the server hook
         only fills the field when it is absent, so whichever runs first wins.
         Security is unchanged — the token is an unguessable per-order capability,
         and a client can only ever weaken the protection of its OWN order. */
      if(payload && !payload.trackingToken) payload.trackingToken = this.newTrackingToken();
      const created = await this.app.service('orders').create(payload);
      // Remember the order just placed so a "track your order" affordance can
      // appear in the header — for GUESTS too, who have no account to look it
      // up under. Same idea as the RN apps' persisted latestOrderId.
      this.setLatestOrder(created?._id, created?.trackingToken);
      return created;
    }

    //The 4am timer archives every order older than the start of LAST month out of `orders`
    //and into `ordersprocessed` (timer.class.js moveDoc). Reading `orders` alone therefore
    //gave a rolling 1-2 month window, not the customer's history: loyalty turnover silently
    //collapsed on the 1st of each month, so any campaign whose `completion` exceeded roughly
    //two months of that customer's spend could never be reached — and nothing logged it.
    //Union both collections, exactly as the admin's data.service ordersPerUser$ already does.
    //The archive leg degrades to [] instead of throwing: this runs inside the order-commit
    //try in loadingOrderModal, so a failed archive read must not fail the order — it just
    //under-counts, which is the behaviour that shipped before this fix.
    ordersPerUser = async() => {
      const query = {
        $paginate: false,
        customerId: this.user._id,
        $sort: {createdAt: -1},
      };
      const [live, archived] = await Promise.all([
        this.app.service('orders').find({ query: {...query} }),
        this.app.service('ordersprocessed').find({ query: {...query} }).catch(() => [])
      ]);
      //moveDoc creates in the target BEFORE removing from the source and preserves _id, so an
      //order can exist in both for a moment. De-duplicate: a double-counted total would hand
      //out a loyalty reward the customer never earned.
      const byId = new Map();
      for(const order of [...(live || []), ...(archived || [])]){
        if(order?._id) byId.set(String(order._id), order);
      }
      return [...byId.values()].sort((a, b) => (+b?.createdAt || 0) - (+a?.createdAt || 0));
    }

    //The orders that count towards LOYALTY: the customer's own orders, plus dine-in table orders the till
    //attributed to them (loyaltyCustomerId, stamped loyaltyVerified by the server only when a real till
    //assignment backs it). Kept apart from ordersPerUser, which also feeds order history and reviews.
    //Mirrors the server's spend query (feathers-server services/loyalty/spend.js) so the progress bar and
    //the app's own accrual agree with the rewards the till mints. On a server that predates till
    //attribution no order carries those fields and this reads exactly like ordersPerUser.
    loyaltyOrdersPerUser = async() => {
      const query = {
        $paginate: false,
        $or: [
          {customerId: this.user._id, loyaltyCustomerId: {$exists: false}},
          {loyaltyCustomerId: this.user._id, loyaltyVerified: true}
        ],
        $sort: {createdAt: -1},
      };
      const [live, archived] = await Promise.all([
        this.app.service('orders').find({ query: {...query} }),
        this.app.service('ordersprocessed').find({ query: {...query} }).catch(() => [])
      ]);
      //Same de-duplication as ordersPerUser: moveDoc can leave an order in both collections for a moment.
      const byId = new Map();
      for(const order of [...(live || []), ...(archived || [])]){
        if(order?._id) byId.set(String(order._id), order);
      }
      return [...byId.values()].sort((a, b) => (+b?.createdAt || 0) - (+a?.createdAt || 0));
    }

    //Has this customer ever ordered before? Unions the archive, because the 4am timer moves older
    //orders out of `orders` and a long-standing customer would otherwise read as a first-timer.
    //$limit:0 returns only the count. The shared guest can never qualify - every logged-out visitor
    //IS that one account, so it is permanently past its first order; that is what turns a
    //first-order coupon into a reason to register.
    isFirstOrderEligible = async() => {
      if(!this.user?._id) return false;
      if(`${this.user?.email ?? ''}`.trim().toLowerCase() === String(DEFAULT_EMAIL).trim().toLowerCase()) return false;
      const query = { customerId: this.user._id, $limit: 0 };
      try{
        const [live, archived] = await Promise.all([
          this.app.service('orders').find({ query: {...query} }),
          this.app.service('ordersprocessed').find({ query: {...query} }).catch(() => ({ total: 0 }))
        ]);
        return ((live?.total ?? 1) + (archived?.total ?? 0)) === 0;
      }catch(error){
        return false;   //never GRANT the discount on a failed check
      }
    }

    tableOrdersPerUser = async() => {
      return await this.app.service('orders')   
      .find({
          query: { 
            $paginate: false,
            "order.customerId": this.user._id
          }
        });
    }

  async popularProducts(){
    return await this.app.service('popular')
    .find({ query: { channel: this.isTableOrder() ? 'venue' : 'delivery' } });
  }
    
  getSettings = async() => {
    return await this.app.service('settings')   
    .find({
      query: { 
        adminId: ADMIN_ID  
      }
    });
  }

  translate = (lang, textEL, textEN) => {
    return (this.language === lang) && textEN ?  textEN : textEL
  }

  // Greek-fiscal unless EXPLICITLY non-Greek: legacy tenants (masterLanguage/country unset) stay Greek.
  isGreekFiscal = () => ((this.settings?.masterLanguage || 'el') === 'el') && ((this.settings?.country || 'el') === 'el');

  /* Phone validation for the Yup FORM schemas (checkout phone / companyPhone / deliveryPhone and
     the contact form). Driven by settings.country, NOT by isGreekFiscal(): fiscal regime is about
     where the receipt is issued, while a phone number's shape is about where the SHOP is - a
     Greek-language shop trading in the UK still takes UK numbers.

     Country UNSET falls back to 'el', so the ~125 legacy Greek tenants that predate the key keep
     exactly today's behaviour. The Greek branch is the PHONE_REGEX literal unchanged, so every
     number a Greek customer can type today still passes - this must never tighten, or Greek
     tenants silently lose orders at checkout.

     Non-Greek tenants get the same international rule Utils.validatePhone already applies at
     REGISTRATION (strip separators, one optional +, 7-15 digits). Before this, checkout and
     registration disagreed: a UK customer could register with 07911123456 and then be blocked
     from paying by a regex that never accepted their number. */
  phoneIsValid = (value) => {
    const raw = `${value ?? ""}`.trim();
    if(!raw) return false;
    const country = String(this.settings?.country || 'el').toLowerCase();
    if(country === 'el') return PHONE_REGEX.test(raw);
    return Utils.validatePhone(raw, false);
  }

  /* What Viva prints on the buyer's BANK STATEMENT. It was hardcoded "BringFood", so every
     shop on the platform billed its customers under a company they had never heard of -
     which is what "I don't recognise this charge" chargebacks are made of.

     Driven from settings.companyName (a dashboard field, live-synced) rather than a build
     constant, because public/constants.js is per-tenant and regenerated by the deploy
     pipeline, so a value placed there by hand does not survive.

     Viva caps the field at 13 characters. Cutting blind would give a shop called
     "AZEL COFFEE BAR" the descriptor "AZEL COFFEE B", so the cut falls back to the last
     word boundary and yields "AZEL COFFEE". A single word longer than the cap has no
     boundary to fall back to and is simply truncated. */
  cardDescriptor = () => {
    const raw = `${this.settings?.companyName ?? ""}`.trim()
      || `${this.settings?.companyLegalName ?? ""}`.trim();
    if(!raw) return "Online order";
    if(raw.length <= 13) return raw;
    const cut = raw.slice(0, 13);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim();
  }

  formatMoney = (value) => {
    if(value === null || value === undefined) value = "";
    const code = String(this.settings?.currency || "EUR").toUpperCase();
    if(code === "GBP") return `£${value}`;
    if(code === "USD") return `$${value}`;
    return `${value}€`;
  }

  // Tenant-local IANA timezone; Greek tenants (unset) stay on Athens.
  timezone = () => tenantTimezone(this.settings);
  displayLocale = () => displayLocale(this.language || this.settings?.masterLanguage);
  formatDate = value => formatDate(value, this.language || this.settings?.masterLanguage, this.timezone());

  /* ---- Google geo gates -------------------------------------------------
     Same shape as the customer RN app (BringFood_Android feathersStore.js:231/236)
     so the two customer clients gate identically.

     DELIBERATELY NO FALLBACK to the bundled AppDate build key. The RN apps ship
     the key inside a binary; this bundle is a PUBLIC web page, so inheriting the
     fallback would spend AppDate's Google quota for every tenant that never
     registered a key. A tenant with no key simply gets today's plain inputs.

     canGeocode  -> address autocomplete / geocoding (needs the tenant's own key)
     canShowMap  -> anything that RENDERS TILES; additionally needs the IS_GEO
                    opt-in, which is how the delivery-boy app gates billable
                    surfaces (BringFood_DB_Android feathersStore.js:762).
     Both read the backend settings doc (live-synced via the 'patched' listener),
     never a build-time constant, so geo can be switched off without a redeploy. */
  tenantMapsKey = () => `${this.settings?.GOOGLE_MAPS_KEY ?? ""}`.trim();

  canGeocode = () => this.tenantMapsKey().length > 0;

  canShowMap = () => Boolean(this.settings?.IS_GEO) && this.canGeocode();

  /* Live courier position for ONE order. Deliberately POLLED, not subscribed:
     the server service emits no events, so no driver position is ever
     broadcast to a channel. Returns
       {orderId, status, deliveryStatus, delivered, hasDriver, position|null}
     Requires the `ordertracking` service to be deployed on the server; callers
     must treat a rejection as "tracking unavailable", not as an error state. */
  getOrderTracking = async (orderId, trackingToken) => {
    // The token is the capability for guest/aggregator orders (customerId does
    // not identify a person here). Sent as a query param so it works over both
    // Socket.IO and REST.
    return await this.app.service('ordertracking')
      .get(orderId, trackingToken ? { query: { trackingToken } } : {});
  }

  _translate = (textEL, textTR) => {
    if(this.language === this.settings.masterLanguage)return textEL;
    else return textTR ? textTR : textEL;
  }

  _translateName = (item) => {
    if(this.language === this.settings.masterLanguage)return item?.name ?? "";
    else return item?.nameTranslations?.find(tr => tr?.code === this.language)?.text ?? item?.name;
  }

  _translateTitle = (item) => {
    if(this.language === this.settings.masterLanguage)return item?.title ?? "";
    else return item?.titleTranslations?.find(tr => tr?.code === this.language)?.text ?? item?.title;
  }

  _translateNameAttr = (item) => {
    if(this.language === this.settings.masterLanguage)return item?.name ?? "";
    else return item?.translations?.find(tr => tr?.code === this.language)?.text ?? item?.name;
  }

  _translateAddress = () => {
    if(this.language === this.settings.masterLanguage)return this.settings?.address ?? "";
    else return this.settings?.addressTranslations?.find(tr => tr?.code === this.language)?.text ?? this.settings?.address;
  }

  _translateClosedShopText = () => {
    if(this.language === this.settings.masterLanguage)return this.settings?.closedShopText ?? "";
    else return this.settings?.closedShopTextTranslations?.find(tr => tr?.code === this.language)?.text ?? this.settings?.closedShopText;
  }

  _translateShortDescription = () => {
    if(this.language === this.settings.masterLanguage)return this.settings?.shortDescription ?? "";
    else return this.settings?.shortDescriptionTranslations?.find(tr => tr?.code === this.language)?.text ?? this.settings?.shortDescription;
  }

  _translateProductShortDescription = (item) => {
    if(this.language === this.settings.masterLanguage)return item?.shortDescription ?? "";
    else return item?.shortDescriptionTranslations?.find(tr => tr?.code === this.language)?.text ?? item?.shortDescription;
  }

  _translateCategoryName = (element) => {
    if(this.language === this.settings.masterLanguage)return element?.name ?? "";
    else return element.options.categoryNameTranslations?.find(tr => tr?.code === this.language)?.text ?? element.name;
  }

   //----> Printer translations   

   translateFeature = (feature, printerLang) => {
    if(printerLang !== this.settings.masterLanguage){
      const word = feature?.translations?.find(tr => tr?.code === printerLang)?.text ?? feature?.name;
      if(word)return word;
    }
    return feature?.name
  }

  translateAttribute = (attribute, printerLang) => {
    if(printerLang !== this.settings.masterLanguage){
      const titleWord = attribute?.titleTranslations?.find(tr => tr?.code === printerLang)?.text ?? attribute?.title;
      const nameWord = attribute?.translations?.find(tr => tr?.code === printerLang)?.text ?? attribute?.name;
      if(titleWord && nameWord)return titleWord + ": " + nameWord + " ";
    }
    return  attribute?.title + ": " + attribute?.name + " ";
  }
  
  translateAttributeTitle = (attribute, printerLang) => {
    if(printerLang !== this.settings.masterLanguage){
      const titleWord = attribute?.titleTranslations?.find(tr => tr?.code === printerLang)?.text ?? attribute?.title;
      if(titleWord)return titleWord;
    }
    return  attribute?.title;
  }  

  translateAttributeName = (attribute, printerLang) => {
    if(printerLang !== this.settings.masterLanguage){
      const nameWord = attribute?.translations?.find(tr => tr?.code === printerLang)?.text ?? attribute?.name;
      if(nameWord)return nameWord;
    }
    return  attribute?.name;
  }  

  translateProductName = (item, printerLang) => {
    if(printerLang !== this.settings.masterLanguage){      
      const word = item?.nameTranslations?.find(tr => tr?.code === printerLang)?.text ?? item?.product_name;
      if(word)return word;
    }
    return item?.product_name; 
  } 

  translateShortDescription = (item, printerLang) => {
    if(printerLang !== this.settings.masterLanguage){      
      const word = item?.shortDescriptionTranslations?.find(tr => tr?.code === printerLang)?.text ?? item?.product_shortDescription;
      if(word)return word;
    }
    return  item?.product_shortDescription; 
  }

  //--------------------------<


 
  // Returns the stock to validate against: size-specific if defined, otherwise root product stock.
  // Backwards-compatible: products without sizes (or sizes without stock set) use product.stock.
  // When product.centralStock is true, always uses root stock regardless of per-size stock.
  effectiveStock = (product, pickedSize) => {
    const s = pickedSize?.stock;
    return (!product?.centralStock && s !== undefined && s !== null) ? s : (product?.stock ?? 0);
  }

  checkOutOfStock = (itemId, handleStock, stock, quantity, cart, weightedQuantity) => {

    //check stock before adding to cart and after setting quantity
    if(handleStock){
      const cartSimilarItems = cart.filter(i => i.product_id === itemId );
      if(cartSimilarItems){
        const cartSimilarItemsQuantity = cartSimilarItems
          .map(csi => (csi?.product_weightedQuantity > 0 ? csi.product_quantity * csi.product_weightedQuantity : csi.product_quantity))
          .reduce((a, b) => a + b, 0)
        const newQ = +cartSimilarItemsQuantity + +(weightedQuantity > 0 ? quantity * weightedQuantity : quantity);
        if(Math.round((newQ + Number.EPSILON) * 1000) / 1000 > stock)return true;
      }else if(Math.round(((weightedQuantity > 0 ? quantity * weightedQuantity : quantity) + Number.EPSILON) * 1000) / 1000 > stock) return true;
    }
    return false;
  }

  createMessage = async(payload) => {      
    Object.assign(payload, { userId: this.user._id})
    this.user.firstname === "default" &&  Object.assign(payload, {chatId: this.chatId})
    return await this.app.service('messages').create(payload); 
  }

  findMessages = async() => {   
    const query = {$sort: {createdAt: 1}};
    this.user.firstname === "default" ? Object.assign(query, {chatId: this.chatId}) 
    : Object.assign(query, {userId: this.user._id});
    return await this.app.service('messages')   
    .find({query});
  }

  getTable = async(tableId) => {
    if(!this.app) return null; //feathers client not connected yet (e.g. tableId rehydrated at load)
    return await this.app.service('tables').get(tableId);
  }

  findProductsPrinters = async(ids) => {
    if (!ids?.length) return [];
    const result = await this.app.service('products')
      .find({ query: { _id: { $in: ids }, $limit: ids.length + 10, $select: ['_id', 'printers'] } });
    return result?.data || result || [];
  }

  //The Δελτίο parts read (bundle-vat-v2 §3/§5): ONE per document, the catalog rows a grouped line's
  //parts are filed from. Bounded - an unanswered socket call never settles, and the order must not
  //hang on it; a throw (or the timeout) sends the caller down the refused-Δελτίο path. Real ObjectIds
  //only: products before.find converts every $in entry with ObjectId.createFromHexString, so one
  //malformed id would fail the whole find.
  findProductsForVatLookup = async(ids, timeoutMs = 6000) => {
    const valid = [...new Set((ids || []).filter(id => typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id)))];
    if (!valid.length) return [];
    let timer;
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Error(`products.find timed out after ${timeoutMs} ms`)), timeoutMs);
    });
    try {
      const result = await Promise.race([
        this.app.service('products')
          .find({ query: { _id: { $in: valid }, $limit: valid.length + 10, $select: ['_id', 'vat', 'packageVat', 'isCommodity'] } }),
        timeout
      ]);
      return Array.isArray(result) ? result : (Array.isArray(result?.data) ? result.data : []);
    } finally {
      clearTimeout(timer);
    }
  }

  _patchTable = async(tableId, order) => {     
    return await this.app.service('tables').patch(tableId, {order});
  }

  patchTable = async(id, payload) => {
    return await this.app    
    .service('tables')
    .patch(id, payload);
  }

  bookingTables = async() => {
    return await this.app
    .service('tables')
    .find({
      query:{
       bookingDisabled: false,
       package: { $ne: true } //takeaway-section tables are not bookable
      }
    })
  }

  createLocalPrint = async (payload) => {      
    return await this.app 
      .service('localprint')
      .create(payload); 
  }

  createCompany = async(payload) => {
    return await this.app      
    .service('companies')    
    .create(payload);
  }

  // patch, not update: update REPLACES the record, and saveOrUpdateCompany sends only afm, aadeResponse,
  // phone, email, origin and addresses - a checkout with an invoice wiped the company's legal name and form,
  // ΔΟΥ, activity, postal address and the supplier settings the admin had filed.
  updateCompany = async(id, payload) => {
    return await this.app
    .service('companies')
    .patch(id, payload);
  }

  findCompany = async(afm) => {
    return await this.app      
    .service('companies')
    .find({
      query:{                
       afm          
      }
    })  
  }

  getCompany = async(companyId) => {
    return await this.app      
    .service('companies')    
    .get(companyId);
  }

  createSoap = async(afm) => {
    return await this.app      
    .service('soap')    
    .create({afm});
  }
  async patchCompanyAddresses(companyId, addresses){     
    return await this.app.service('companies').patch(companyId, {addresses});
  }  

  async coupons(){
    return await this.app.service('coupons')   
    .find({
        query: { 
         active: true,
        }
      }); 
  }

  getDeltioCounter = async() => {
    return await this.app      
    .service('counters')
    .get("deltio")
  }

  patchCounters = async(id, payload) => {      
    return await this.app
      .service('counters')
      .patch(id, payload);
  }

  postToMyData = async(payload) => {      
    return await this.app
      .service('mydata')
      .create(payload); 
  }

  createLogEntry = async (payload) => {      
    return await this.app
      .service('emaillog')
      .create(payload); 
  }

  saveStateToLocalStorage = () => {
    const fdwaState = {
      hasLocationPermission: this.hasLocationPermission,
      cartLength: this.cartLength,
      showCookies: this.showCookies,
      tableId: this.tableId,
      preOrderBooking: this.preOrderBooking,
      restaurantOpen: this.restaurantOpen,
      showRestaurantMessage: this.showRestaurantMessage,
      discountsArray: this.discountsArray,
      currency: this.currency,
      orderCode: this.orderCode
    } 
    localStorage.setItem('fdwa_state', JSON.stringify(fdwaState));
    localStorage.setItem('fdwa_orderItem', JSON.stringify(this.orderItem));  
  }
}

export default new FeathersStore()